import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { appConfig } from '../config/appConfig';

// Application Insights configuration
export class ApplicationInsightsService {
  private static instance: ApplicationInsightsService;
  private appInsights: ApplicationInsights | null = null;
  private reactPlugin: ReactPlugin;
  private isInitialized = false;

  private constructor() {
    this.reactPlugin = new ReactPlugin();
  }

  public static getInstance(): ApplicationInsightsService {
    if (!ApplicationInsightsService.instance) {
      ApplicationInsightsService.instance = new ApplicationInsightsService();
    }
    return ApplicationInsightsService.instance;
  }

  /**
   * Initialize Application Insights
   */
  public initialize(): void {
    if (this.isInitialized) {
      console.warn('Application Insights already initialized');
      return;
    }

    // Only initialize if we have a connection string or instrumentation key
    const hasConnectionString = !!process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;
    const hasInstrumentationKey = !!process.env.REACT_APP_APPLICATIONINSIGHTS_INSTRUMENTATION_KEY;
    
    if (!hasConnectionString && !hasInstrumentationKey) {
      console.warn('Application Insights not configured - missing connection string or instrumentation key');
      return;
    }

    // Skip initialization in development if analytics is disabled
    if (!appConfig.features.analytics && appConfig.development.debug) {
      console.log('Application Insights disabled in development mode');
      return;
    }

    try {
      // Create ApplicationInsights instance
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING,
          instrumentationKey: process.env.REACT_APP_APPLICATIONINSIGHTS_INSTRUMENTATION_KEY,
          extensions: [this.reactPlugin],
          enableAutoRouteTracking: true,
          disableFetchTracking: false,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true
        }
      });
      
      this.appInsights.loadAppInsights();
      this.isInitialized = true;
      
      console.log('✅ Application Insights initialized successfully');
      
      // Set initial context
      this.setUserContext();
      
      // Track app initialization
      this.trackEvent('AppInitialized', {
        environment: process.env.NODE_ENV || 'development',
        version: process.env.REACT_APP_VERSION || '1.0.0',
        timestamp: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Failed to initialize Application Insights:', error);
    }
  }

  /**
   * Set user context for tracking
   */
  public setUserContext(userId?: string, accountId?: string): void {
    if (!this.isInitialized || !this.appInsights) return;

    try {
      this.appInsights.setAuthenticatedUserContext(
        userId || 'anonymous',
        accountId || 'unifirst'
      );
    } catch (error) {
      console.warn('Failed to set user context:', error);
    }
  }

  /**
   * Track custom events
   */
  public trackEvent(name: string, properties?: Record<string, any>, measurements?: Record<string, number>): void {
    if (!this.isInitialized || !this.appInsights) {
      // Fallback to console logging
      console.log(`📊 Event: ${name}`, { properties, measurements });
      return;
    }

    try {
      this.appInsights.trackEvent({
        name,
        properties: {
          ...properties,
          timestamp: new Date().toISOString(),
          sessionId: this.getSessionId()
        },
        measurements
      });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Track page views
   */
  public trackPageView(name?: string, uri?: string, properties?: Record<string, any>): void {
    if (!this.isInitialized || !this.appInsights) {
      console.log(`📄 Page View: ${name || uri || window.location.pathname}`);
      return;
    }

    try {
      this.appInsights.trackPageView({
        name,
        uri: uri || window.location.href,
        properties: {
          ...properties,
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Track exceptions/errors
   */
  public trackException(error: Error, properties?: Record<string, any>): void {
    if (!this.isInitialized || !this.appInsights) {
      console.error('🚨 Exception:', error, properties);
      return;
    }

    try {
      this.appInsights.trackException({
        exception: error,
        properties: {
          ...properties,
          timestamp: new Date().toISOString(),
          sessionId: this.getSessionId()
        }
      });
    } catch (trackingError) {
      console.error('Failed to track exception:', trackingError);
    }
  }

  /**
   * Track metrics/measurements
   */
  public trackMetric(name: string, average: number, properties?: Record<string, any>): void {
    if (!this.isInitialized || !this.appInsights) {
      console.log(`📈 Metric: ${name} = ${average}`, properties);
      return;
    }

    try {
      this.appInsights.trackMetric({
        name,
        average,
        properties: {
          ...properties,
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('Failed to track metric:', error);
    }
  }

  /**
   * Track dependencies (API calls, external services)
   */
  public trackDependency(
    name: string, 
    data: string, 
    success: boolean, 
    duration: number,
    properties?: Record<string, any>
  ): void {
    if (!this.isInitialized || !this.appInsights) {
      console.log(`🔗 Dependency: ${name} (${duration}ms) ${success ? '✅' : '❌'}`, { data, properties });
      return;
    }

    try {
      this.appInsights.trackDependencyData({
        id: `dep_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name,
        data,
        success,
        duration,
        responseCode: success ? 200 : 500,
        properties: {
          ...properties,
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('Failed to track dependency:', error);
    }
  }

  /**
   * Flush all pending telemetry
   */
  public flush(): void {
    if (this.isInitialized && this.appInsights) {
      this.appInsights.flush();
    }
  }

  /**
   * Get the current session ID
   */
  public getSessionId(): string {
    if (this.isInitialized && this.appInsights) {
      return this.appInsights.context?.session?.id || 'no-session';
    }
    return 'no-session';
  }

  /**
   * Get the React plugin for integration with React Router
   */
  public getReactPlugin(): ReactPlugin {
    return this.reactPlugin;
  }

  /**
   * Check if Application Insights is initialized
   */
  public get initialized(): boolean {
    return this.isInitialized;
  }
}

// Export singleton instance
export const applicationInsights = ApplicationInsightsService.getInstance();

// Export hook for React components
export const useApplicationInsights = () => {
  return {
    trackEvent: (name: string, properties?: Record<string, any>, measurements?: Record<string, number>) => 
      applicationInsights.trackEvent(name, properties, measurements),
    
    trackPageView: (name?: string, uri?: string, properties?: Record<string, any>) => 
      applicationInsights.trackPageView(name, uri, properties),
    
    trackException: (error: Error, properties?: Record<string, any>) => 
      applicationInsights.trackException(error, properties),
    
    trackMetric: (name: string, average: number, properties?: Record<string, any>) => 
      applicationInsights.trackMetric(name, average, properties),
    
    trackDependency: (name: string, data: string, success: boolean, duration: number, properties?: Record<string, any>) => 
      applicationInsights.trackDependency(name, data, success, duration, properties),
    
    setUserContext: (userId?: string, accountId?: string) => 
      applicationInsights.setUserContext(userId, accountId),
    
    initialized: applicationInsights.initialized
  };
};
